import { FocusTrap } from 'focus-trap-react';
import { ArrowLeft, Images, Rotate3d, Video } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type React from 'react';
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import type { ElasticVehicleMedia } from '@//app/api/search/vehicles/[id]';
import { Container } from '@//components/Container';
import { Portal } from '@//components/Portal';
import styles from './GalleryModal.module.scss';
import GalleryModal360 from './GalleryModal360';
import GalleryModalImage from './GalleryModalImage';
import GalleryModalVideo from './GalleryModalVideo';
import { transformMedia } from './utils';

const GalleryModalSphere = lazy(() => import('./GalleryModalSphere'));

interface GalleryModalProps {
  vehicleTitle: string;
  media?: ElasticVehicleMedia[];
  isOpen: boolean;
  onClose: () => void;
}

const GalleryModal: React.FC<GalleryModalProps> = ({ vehicleTitle, media, isOpen, onClose }) => {
  const t = useTranslations('pages.item.imageGallery.modal');
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const [swipeState, setSwipeState] = useState({
    startY: 0,
    currentY: 0,
    isDragging: false,
    canSwipe: false,
  });
  const [exitTransform, setExitTransform] = useState(0);
  // True for the duration of a swipe-release "snap-back" — used to apply an
  // inline transition only during the snap, not at mount.
  const [isSnappingBack, setIsSnappingBack] = useState(false);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    const touch = e.touches[0];
    const scrollTop = modalRef.current?.scrollTop || 0;

    setSwipeState({
      startY: touch.clientY,
      currentY: touch.clientY,
      isDragging: true,
      canSwipe: scrollTop === 0, // Only allow swipe when at top
    });
  }, []);

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!swipeState.isDragging || !swipeState.canSwipe) return;

      const touch = e.touches[0];
      const deltaY = touch.clientY - swipeState.startY;

      // Only track downward swipes
      if (deltaY > 0) {
        setSwipeState((prev) => ({
          ...prev,
          currentY: touch.clientY,
        }));

        // Prevent default scrolling when swiping down from top
        e.preventDefault();
      }
    },
    [swipeState.isDragging, swipeState.canSwipe, swipeState.startY],
  );

  const handleTouchEnd = useCallback(() => {
    if (!swipeState.isDragging || !swipeState.canSwipe) {
      setSwipeState((prev) => ({ ...prev, isDragging: false }));
      return;
    }

    const deltaY = swipeState.currentY - swipeState.startY;
    const threshold = 100; // Minimum swipe distance to close

    if (deltaY > threshold) {
      // Keep the current transform during exit animation
      setExitTransform(deltaY);
      onClose();
    } else if (deltaY > 0) {
      // Released without crossing threshold — animate the snap back to rest.
      setIsSnappingBack(true);
      setTimeout(() => setIsSnappingBack(false), 220);
    }

    setSwipeState({
      startY: 0,
      currentY: 0,
      isDragging: false,
      canSwipe: false,
    });
  }, [swipeState, onClose]);

  useEffect(() => {
    previousFocusRef.current = document.activeElement as HTMLElement;

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      // Reset exit transform when opening
      setExitTransform(0);
      document.addEventListener('keydown', handleEscapeKey);
      document.addEventListener('touchstart', handleTouchStart, {
        passive: false,
      });
      document.addEventListener('touchmove', handleTouchMove, {
        passive: false,
      });
      document.addEventListener('touchend', handleTouchEnd, { passive: true });
    }

    return () => {
      if (previousFocusRef.current) {
        //previousFocusRef.current.focus();
      }
      document.removeEventListener('keydown', handleEscapeKey);
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isOpen, onClose, handleTouchStart, handleTouchMove, handleTouchEnd]);

  const swipeProgress =
    swipeState.isDragging && swipeState.canSwipe
      ? Math.max(0, swipeState.currentY - swipeState.startY)
      : exitTransform;

  // Match the durations defined in GalleryModal.module.scss
  // (--motion-duration-base for enter, --motion-duration-fast for exit).
  const transitionDuration = { appear: 200, enter: 200, exit: 160 };

  const transformedMedia = useMemo(
    () => transformMedia(media, vehicleTitle),
    [media, vehicleTitle],
  );

  // Count the number of available media categories
  const categoriesCount = useMemo(() => {
    let count = 0;
    if (transformedMedia.statistics.images > 0) count++;
    if (transformedMedia.statistics.videos > 0) count++;
    if (transformedMedia.statistics[360] + transformedMedia.statistics.spheres > 0) count++;
    return count;
  }, [transformedMedia]);

  return (
    <Portal>
      <CSSTransition
        in={isOpen}
        appear
        timeout={transitionDuration}
        unmountOnExit
        classNames={{
          appear: styles.dialogAppear,
          appearActive: styles.dialogAppearActive,
          enter: styles.dialogEnter,
          enterActive: styles.dialogEnterActive,
          exit: styles.dialogExit,
          exitActive: styles.dialogExitActive,
        }}
        nodeRef={modalRef}
      >
        <FocusTrap active={isOpen} focusTrapOptions={{ clickOutsideDeactivates: true }}>
          <div
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-label={t('label')}
            className={styles.dialog}
            style={{
              transform: swipeProgress > 0 ? `translateY(${swipeProgress}px)` : undefined,
              opacity: swipeProgress > 0 ? Math.max(0.3, 1 - swipeProgress / 300) : undefined,
              // Only set an inline transition when this component is actively
              // managing transform/opacity. At mount the value is `undefined`,
              // so the CSSTransition enter/appear classes own the animation.
              transition: swipeState.isDragging
                ? 'none'
                : isSnappingBack
                  ? 'transform 200ms ease-out, opacity 200ms ease-out'
                  : undefined,
            }}
          >
            <Container>
              <header className={styles.header}>
                <button
                  type="button"
                  onClick={onClose}
                  title={t('closeButton')}
                  className={styles.closeButton}
                  data-testid="gallery-modal-close-button"
                >
                  <ArrowLeft aria-hidden="true" />
                  <span className="sr-only">{t('closeButton')}</span>
                </button>
                {categoriesCount > 1 && (
                  <nav className={styles.nav} aria-label={t('navigation.label')}>
                    <ul className={styles.actions}>
                      {transformedMedia.statistics.images > 0 && (
                        <li>
                          <a
                            href="#gallery-modal-images"
                            title={t('navigation.images.title')}
                            className={styles.skipLink}
                          >
                            <Images aria-hidden="true" />
                            <span className={styles.skipLinkShort} aria-hidden="true">
                              {transformedMedia.statistics.images}
                            </span>
                            <span className={styles.skipLinkLong}>
                              {t('navigation.images.label', {
                                count: transformedMedia.statistics.images,
                              })}
                            </span>
                          </a>
                        </li>
                      )}
                      {transformedMedia.statistics.videos > 0 && (
                        <li>
                          <a
                            href="#gallery-modal-videos"
                            title={t('navigation.videos.title')}
                            className={styles.skipLink}
                          >
                            <Video aria-hidden="true" />
                            <span className={styles.skipLinkShort} aria-hidden="true">
                              {transformedMedia.statistics.videos}
                            </span>
                            <span className={styles.skipLinkLong}>
                              {t('navigation.videos.label', {
                                count: transformedMedia.statistics.videos,
                              })}
                            </span>
                          </a>
                        </li>
                      )}
                      {transformedMedia.statistics[360] + transformedMedia.statistics.spheres >
                        0 && (
                        <li>
                          <a
                            href="#gallery-modal-360"
                            title={t('navigation.360.title')}
                            className={styles.skipLink}
                          >
                            <Rotate3d aria-hidden="true" />
                            <span className={styles.skipLinkShort} aria-hidden="true">
                              {transformedMedia.statistics['360'] + transformedMedia.spheres.length}
                            </span>
                            <span className={styles.skipLinkLong}>
                              {t('navigation.360.label', {
                                count:
                                  transformedMedia.statistics['360'] +
                                  transformedMedia.spheres.length,
                              })}
                            </span>
                          </a>
                        </li>
                      )}
                    </ul>
                  </nav>
                )}
              </header>
              <div className={styles.body}>
                {transformedMedia.statistics.images > 0 && (
                  <ul
                    className={styles.list}
                    aria-label={t('imageSection')}
                    id="gallery-modal-images"
                  >
                    {transformedMedia.images.map((image, index) => (
                      <li key={`gallery-modal-item-${image.src}-${index}`} className={styles.item}>
                        <GalleryModalImage image={image} />
                      </li>
                    ))}
                  </ul>
                )}
                {transformedMedia.statistics.videos > 0 && (
                  <ul
                    className={styles.list}
                    aria-label={t('videoSection')}
                    id="gallery-modal-videos"
                  >
                    {transformedMedia.videos.map((video, index) => (
                      <li key={`gallery-modal-video-${video.url}-${index}`} className={styles.item}>
                        <GalleryModalVideo video={video} />
                      </li>
                    ))}
                  </ul>
                )}
                {transformedMedia.statistics[360] + transformedMedia.statistics.spheres > 0 && (
                  <ul className={styles.list} aria-label={t('360Section')} id="gallery-modal-360">
                    {transformedMedia[360].map((images, index) => (
                      <li
                        key={`gallery-modal-360-${images?.[0]?.src}-${index}`}
                        className={styles.item}
                      >
                        <GalleryModal360 images={images} id={`360-viewer-${index}`} />
                      </li>
                    ))}
                    {transformedMedia.spheres.map((image, index) => (
                      <li
                        key={`gallery-modal-sphere-${image.src}-${index}`}
                        className={styles.item}
                      >
                        <Suspense fallback={<div style={{ height: 600 }}>Loading 360° view…</div>}>
                          <GalleryModalSphere image={image} id={`sphere-viewer-${index}`} />
                        </Suspense>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Container>
          </div>
        </FocusTrap>
      </CSSTransition>
    </Portal>
  );
};

export default GalleryModal;
